<template lang="html">
  <section>
    <name-input
      :all-des="allDes"
      :is-ready="!loadingData"
      :should-focus="!storedDataElement.name"
      :name="storedDataElement.name"
      :de-id="storedDataElement.id"
      @on-name-input-changed="onDeNameChanged"
    ></name-input>

    <element-description
      v-if="!loadingData"
      :elem-description="storedDataElement.description"
      @on-element-description-changed="onDeDescriptionChanged"
    ></element-description>

    <div class="crm-fields-container" v-if="!loadingData">
      <label class="query-label query-object">
        {{ getMessageText(messages.OBJECT) }}
      </label>
      <div class="obj-select">
        <search-box
          v-if="!fetchObjectsFailed"
          ref="objectSearchInCRMQuery"
          id="crm-object-search"
          :has-icon="false"
          :box-type="'deEditModal'"
          :list="filteredObjects"
          :value="storedDataElement.selectedObject"
          @on-search-box-item-selected="onObjectSearchItemSelected"
        />

        <button
          v-if="fetchObjectsFailed"
          class="modal-btn modal-save-btn retry-btn no-user-select"
          :disabled="loadingData"
          @click="describeObjects"
        >
          {{ getMessageText(messages.RETRY) }}
        </button>
      </div>
    </div>

    <div class="crm-fields-container" v-if="!loadingData">
      <label class="query-label" v-if="fieldsReady">
        {{ $t("fields") }}
      </label>
      <div class="crm-fields-search" v-if="fieldsReady">
        <search-box
          class="multi-select"
          v-if="!fetchFieldsFailed"
          id="crmFieldsMultiSelect"
          :multi-select="true"
          :box-type="'deEditModal'"
          :has-icon="true"
          :list="fieldsForMultiSelect"
          :values="storedDataElement.selectedItems"
          @on-search-box-item-selected="onSearchBoxItemSelected"
        />

        <button
          v-if="fetchFieldsFailed"
          class="modal-btn modal-save-btn retry-btn no-user-select"
          :disabled="loadingData"
          @click="describeSelectedObject(storedDataElement.selectedObject)"
        >
          {{ getMessageText(messages.RETRY) }}
        </button>
      </div>
    </div>

    <div class="crm-fields-container" v-if="!loadingData">
      <label
        class="query-label"
        v-if="fieldsReady && storedDataElement.selectedObject"
      >
        {{ $t("limit") }}
      </label>
      <div
        class="query-limit"
        v-if="fieldsReady && storedDataElement.selectedObject"
      >
        <b-form-input
          :value="initialLimit"
          v-model="storedDataElement.limit"
          type="number"
          min="0"
          @change="onLimitChanged"
        ></b-form-input>
      </div>
    </div>

    <div class="crm-fields-container" v-if="!loadingData">
      <label class="query-label" v-if="fieldsReady">
        {{ $t("sort") }}
      </label>
      <div class="query-sort" v-if="fieldsReady">
        <sort-field
          :initial-sort="initialSort"
          :fields="fields"
          @on-sort-fields-change="onSortFieldsChange"
        />
      </div>
    </div>

    <div class="crm-fields-container" v-if="!loadingData">
      <label class="query-label" v-if="fieldsReady">
        {{ $t("where") }}
      </label>
      <where-group
        v-if="fieldsReady"
        ref="whereGroup"
        :id="groupInfo.id"
        :info="groupInfo"
        :fields="fieldsForWhereClause"
        :where="initialWhere"
        :data-element="de"
        :all-des="allDes"
        :app-type="appContent.type"
        @on-draggable-item-drop="onDraggableItemDrop"
        @on-where-clause-update="onWhereClauseUpdate"
      />
    </div>

    <transition name="loader">
      <div class="loader-wrapper" v-if="loadingData">
        <div class="loader"></div>
      </div>
    </transition>
  </section>
</template>

<script lang="js">
  import { mapState, mapActions } from "vuex";
  import SearchBox from "@/components/SearchBox";
  import SfGlobalObjects from "@/utils/sf-global-objects";
  import {MUTATION_NAME, TOAST_CONFIG} from "@/static/global-variables";
  import WhereGroup from "@/components/queries/crm-queries/WhereGroup";
  import SortField from "@/components/queries/crm-queries/SortField";
  import utils from "@/utils";
  import NameInput from "@/components/NameInput";
  import ElementDescription from "@/components/ElementDescription";
  import expressionBuilder from "@/views/drag-drop-pages/components/utils/expression-builder";

  export default  {
    name: "crm-edit",
    components: {
      ElementDescription,
      NameInput,
      SearchBox,
      WhereGroup,
      SortField
    },
    props: {
      appId: {
       type: String
      },
      allDes: {
        type: Object
      },
      de: {
        type: Object
      }
    },
    computed: {
      ...mapState({
        messages: "messages"
      }),
      fieldsForWhereClause() {
        return this.fields.filter(field => {
          return !field.hideFromWhereClause;
        });
      },
      fieldsForMultiSelect() {
        return this.fields.filter(field => {
          return !field.hideFromMultiSelect;
        });
      },
      fieldsReady() {
        return Boolean(this.fields.length)
      },
      appContent() {
        return JSON.parse(JSON.stringify(expressionBuilder.getAppContent(this.appId)));
      }
    },
    mounted() {
      this.init();
    },
    data() {
      return {
        storedDataElement: {
          appId: this.appId,
          description: this.de.description,
          id: this.de.id,
          type: this.de.type,
          name: this.de.name,
          object: this.de.object,
          selectedObject: this.de.object || null,
          selectedItems: [],
          sort: Array.isArray(this.de.sort)? this.de.sort : [],
          where: this.de.where || {},
          limit: this.de.limit
        },
        filteredObjects: [],
        fields: [],
        initialWhere: this.de.where || {}, // for initial setup for where clause and avoid loop on update
        initialSort: Array.isArray(this.de.sort)? this.de.sort : [], // for initial setup for sort and avoid loop on update
        initialLimit: this.de.limit,  // for initial setup for limit value and avoid loop on update
        loadingData: false,
        fetchObjectsFailed: null,
        fetchFieldsFailed: null,
        // initial where group
        groupInfo: {
          id: "group-1"
        }
      };
    },
    methods: {
      ...mapActions({
        setDataElement: MUTATION_NAME.SET_EDIT_DATA_ELEMENT
      }),
      init() {
        if (!this.de.object) {
          this.describeObjects();
        } else {
          if (this.de.fields) {
            this.storedDataElement.selectedItems = Object.keys(this.de.fields).map(
              key => {
                return {
                  name: key,
                  type: this.de.fields[key].dataType,
                  dataType: this.de.fields[key].dataType,
                  label: this.de.fields[key].label,
                  isPicklist: this.de.fields[key].isPicklist
                };
              }
            );
            this.describeSelectedObject(this.de.object);
          } else {
            if (this.de.object.name) {
              this.describeSelectedObject(this.de.object);
            } else {
              this.fields = [];
            }
          }
        }
        this.setDataElement(this.storedDataElement);
      },
      describeObjects() {
        this.loadingData = true;
        SfGlobalObjects.fetchAvailableObjects().then(
          res => {
            this.filteredObjects = res;
            this.loadingData = false;
          },
          () => {
            this.fetchObjectsFailed = true;
            this.displayFetchError();
          }
        );
      },
      displayFetchError() {
        this.loadingData = false;
        this.$bvToast.toast(
          this.getMessageText(this.messages.SFDC_METADATA_ERROR),
          TOAST_CONFIG.BOTTOM_CENTER_ERROR
        );
      },
      onSortFieldsChange(sort) {
        this.storedDataElement.sort = sort;
        this.setDataElement(this.storedDataElement);
      },

      describeSelectedObject(obj) {
        this.loadingData = true;
        SfGlobalObjects.describeObject(obj.name).then(
          resp => {
            SfGlobalObjects.fetchObjectMetaData(obj.name).then(
              meta => {
                this.fields = utils.mergeMetadataIntoFields(
                  resp.fields,
                  meta.records,
                  true
                );
                this.loadingData = false;
              },
              () => {
                this.fetchFieldsFailed = true;
                this.displayFetchError();
              }
            );
          },
          () => {
            this.fetchFieldsFailed = true;
            this.displayFetchError();
          }
        );
      },
      onSearchBoxItemSelected(selectedItems) {
        this.storedDataElement.selectedItems = selectedItems;
      },
      onObjectSearchItemSelected(obj) {
        this.storedDataElement.selectedObject = obj;
        if (obj && obj.name) {
          if(obj.name !== this.storedDataElement.object.name) {
            this.storedDataElement.selectedItems = [];
            this.storedDataElement.sort = [];
            this.storedDataElement.where = {};
            this.storedDataElement.limit = null;
            this.initialWhere = {};
            this.initialSort = [];
            this.initialLimit = null;
          }
          this.storedDataElement.object = obj;
          this.describeSelectedObject(obj);
          this.setDataElement(this.storedDataElement);
        } else {
          this.describeObjects();
          this.fields = [];
          this.storedDataElement.selectedItems = [];
        }
      },
      onLimitChanged() {},
      onDraggableItemDrop() {
        // trigger removal of drop over effect, starting from root
        this.$refs.whereGroup.removeDropOverEffect();
      },
      onWhereClauseUpdate() {
        this.storedDataElement.where = this.$refs.whereGroup.getWhereClauses();
        this.setDataElement(this.storedDataElement);
      },
      onDeNameChanged(isValidName, nameInput) {
        this.storedDataElement.name = nameInput;
        this.$emit("on-de-name-changed", isValidName);
      },
      onDeDescriptionChanged(input) {
        this.storedDataElement.description = input;
      }
    }
  }
</script>

<style scoped lang="scss">
  .query-limit {
    max-width: 20%;
  }

  .query-limit input {
    font-size: 0.75rem;
  }

  .crm-fields-search {
    flex: auto;
    max-width: fit-content;
  }

  .query-sort {
    flex: 1 0 auto;
  }

  .option-row {
    font-size: 0.75rem;
  }

  .option-label,
  .option-dev-name,
  .option-type {
    display: flex;
  }

  .option-left {
    flex-basis: 25%;
  }

  .option-dev-name,
  .option-type {
    margin-top: 0.25rem;
  }
</style>
